import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Navigation from '@/components/Navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, ChevronRight, ChevronDown, ChevronUp, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { copy } from '@/copy';

const GuidanceSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mt-12 pt-8 border-t border-[#E5E5E5]">
      {/* Section Header */}
      <div className="mb-6">
        <h2 className="font-serif text-xl text-[#1D2D44] mb-1">{copy.reflections.guidanceSection}</h2>
        <p className="text-sm text-[#424E59]">{copy.reflections.guidanceDescription}</p>
      </div>

      {/* Guide Card */}
      <Card className="border-none shadow-md rounded-2xl overflow-hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left p-6 flex items-center justify-between hover:bg-[#F5F5F5]/50 transition-colors"
          data-testid="guidance-toggle-btn"
        >
          <div>
            <h3 className="font-serif text-lg text-[#1D2D44]">{copy.guide.title}</h3>
            <p className="text-sm text-[#424E59] mt-1">{copy.guide.seriesTitle}</p>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#424E59]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#424E59]" />
          )}
        </button>

        {isExpanded && (
          <CardContent className="px-6 pb-8 pt-0">
            <div className="border-t border-[#E5E5E5] pt-6">
              {/* Brand Header */}
              <div className="text-center mb-8">
                <p className="font-serif text-lg text-[#1D2D44] mb-2">
                  Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-0.5" strokeWidth={1.5} /> Connection
                </p>
                <p className="text-[#5E7F63] text-sm">{copy.guide.seriesTitle}</p>
              </div>

              {/* How to Use */}
              <div className="mb-10 pb-8 border-b border-[#E5E5E5]">
                <h4 className="font-serif text-lg text-[#1D2D44] mb-4">{copy.guide.howToUse.title}</h4>
                <div className="space-y-3 text-[#1D2D44] leading-relaxed">
                  <p>{copy.guide.howToUse.p1}</p>
                  <p>{copy.guide.howToUse.p2}</p>
                  <p>{copy.guide.howToUse.p3}</p>
                  <p className="pt-2">{copy.guide.howToUse.p4}</p>
                  <p>{copy.guide.howToUse.p5}</p>
                  <p>{copy.guide.howToUse.p6}</p>
                  <p>{copy.guide.howToUse.p7}</p>
                </div>
              </div>

              {/* Part 1 */}
              <div className="mb-10 pb-8 border-b border-[#E5E5E5]">
                <h4 className="font-serif text-lg text-[#1D2D44] mb-4">{copy.guide.part1.title}</h4>
                <div className="space-y-3 text-[#1D2D44] leading-relaxed">
                  <p>{copy.guide.part1.p1}</p>
                  <p>{copy.guide.part1.p2}</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-[#1D2D44]">
                    {copy.guide.part1.list.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p>{copy.guide.part1.p3}</p>
                  <p>{copy.guide.part1.p4}</p>
                  <p className="font-medium text-[#1D2D44]">{copy.guide.part1.p5}</p>
                </div>
                <div className="mt-4 bg-[#A3B8A6]/10 rounded-xl p-4">
                  <p className="text-sm text-[#5E7F63] mb-1 italic">Reflection prompt:</p>
                  <p className="text-[#3A4A5A] text-sm">{copy.guide.part1.reflection}</p>
                </div>
              </div>

              {/* Part 2 */}
              <div className="mb-10 pb-8 border-b border-[#E5E5E5]">
                <h4 className="font-serif text-lg text-[#1D2D44] mb-4">{copy.guide.part2.title}</h4>
                <div className="space-y-3 text-[#1D2D44] leading-relaxed">
                  <p>{copy.guide.part2.p1}</p>
                  <p>{copy.guide.part2.p2}</p>
                  <p>{copy.guide.part2.p3}</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-[#1D2D44]">
                    {copy.guide.part2.list1.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p>{copy.guide.part2.p4}</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-[#1D2D44]">
                    {copy.guide.part2.list2.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p>{copy.guide.part2.p5}</p>
                  <p>{copy.guide.part2.p6}</p>
                  <p className="font-medium text-[#1D2D44]">{copy.guide.part2.p7}</p>
                </div>
                <div className="mt-4 bg-[#A3B8A6]/10 rounded-xl p-4">
                  <p className="text-sm text-[#5E7F63] mb-1 italic">Reflection prompt:</p>
                  <p className="text-[#3A4A5A] text-sm">{copy.guide.part2.reflection}</p>
                </div>
              </div>

              {/* Part 3 */}
              <div className="mb-10 pb-8 border-b border-[#E5E5E5]">
                <h4 className="font-serif text-lg text-[#1D2D44] mb-4">{copy.guide.part3.title}</h4>
                <div className="space-y-3 text-[#1D2D44] leading-relaxed">
                  <p>{copy.guide.part3.p1}</p>
                  <p>{copy.guide.part3.p2}</p>
                  <p>{copy.guide.part3.p3}</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-[#1D2D44]">
                    {copy.guide.part3.list1.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p>{copy.guide.part3.p4}</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-[#1D2D44]">
                    {copy.guide.part3.list2.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p className="font-medium text-[#1D2D44]">{copy.guide.part3.p5}</p>
                </div>
                <div className="mt-4 bg-[#A3B8A6]/10 rounded-xl p-4">
                  <p className="text-sm text-[#5E7F63] mb-1 italic">Reflection prompt:</p>
                  <p className="text-[#3A4A5A] text-sm">{copy.guide.part3.reflection}</p>
                </div>
              </div>

              {/* Part 4 */}
              <div className="mb-10 pb-8 border-b border-[#E5E5E5]">
                <h4 className="font-serif text-lg text-[#1D2D44] mb-4">{copy.guide.part4.title}</h4>
                <div className="space-y-3 text-[#1D2D44] leading-relaxed">
                  <p>{copy.guide.part4.p1}</p>
                  <p>{copy.guide.part4.p2}</p>
                  <p className="font-medium text-[#1D2D44]">{copy.guide.part4.p3}</p>
                  <p>{copy.guide.part4.p4}</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-[#1D2D44]">
                    {copy.guide.part4.list.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p>{copy.guide.part4.p5}</p>
                  <p>{copy.guide.part4.p6}</p>
                </div>
                <div className="mt-4 bg-[#A3B8A6]/10 rounded-xl p-4">
                  <p className="text-sm text-[#5E7F63] mb-1 italic">Reflection prompt:</p>
                  <p className="text-[#3A4A5A] text-sm">{copy.guide.part4.reflection}</p>
                </div>
              </div>

              {/* Part 5 */}
              <div className="mb-10 pb-8 border-b border-[#E5E5E5]">
                <h4 className="font-serif text-lg text-[#1D2D44] mb-4">{copy.guide.part5.title}</h4>
                <div className="space-y-3 text-[#1D2D44] leading-relaxed">
                  <p>{copy.guide.part5.p1}</p>
                  <p>{copy.guide.part5.p2}</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-[#1D2D44]">
                    {copy.guide.part5.list.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p>{copy.guide.part5.p3}</p>
                  <p className="font-medium text-[#1D2D44]">{copy.guide.part5.p4}</p>
                </div>
                <div className="mt-4 bg-[#A3B8A6]/10 rounded-xl p-4">
                  <p className="text-sm text-[#5E7F63] mb-1 italic">Reflection prompt:</p>
                  <p className="text-[#3A4A5A] text-sm">{copy.guide.part5.reflection}</p>
                </div>
              </div>

              {/* Part 6 */}
              <div className="mb-10 pb-8 border-b border-[#E5E5E5]">
                <h4 className="font-serif text-lg text-[#1D2D44] mb-4">{copy.guide.part6.title}</h4>
                <div className="space-y-3 text-[#1D2D44] leading-relaxed">
                  <p>{copy.guide.part6.p1}</p>
                  <p>{copy.guide.part6.p2}</p>
                  <p>{copy.guide.part6.p3}</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-[#1D2D44]">
                    {copy.guide.part6.list.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p>{copy.guide.part6.p4}</p>
                  <p className="font-medium text-[#1D2D44]">{copy.guide.part6.p5}</p>
                </div>
                <div className="mt-4 bg-[#A3B8A6]/10 rounded-xl p-4">
                  <p className="text-sm text-[#5E7F63] mb-1 italic">Reflection prompt:</p>
                  <p className="text-[#3A4A5A] text-sm">{copy.guide.part6.reflection}</p>
                </div>
              </div>

              {/* Part 7 */}
              <div className="mb-10 pb-8 border-b border-[#E5E5E5]">
                <h4 className="font-serif text-lg text-[#1D2D44] mb-4">{copy.guide.part7.title}</h4>
                <div className="space-y-3 text-[#1D2D44] leading-relaxed">
                  <p>{copy.guide.part7.p1}</p>
                  <p className="font-medium text-[#1D2D44]">{copy.guide.part7.p2}</p>
                  <p>{copy.guide.part7.p3}</p>
                  <ul className="list-disc list-inside space-y-1 pl-2 text-[#1D2D44]">
                    {copy.guide.part7.list.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p>{copy.guide.part7.p4}</p>
                </div>
                <div className="mt-4 bg-[#A3B8A6]/10 rounded-xl p-4">
                  <p className="text-sm text-[#5E7F63] mb-1 italic">Reflection prompt:</p>
                  <p className="text-[#3A4A5A] text-sm">{copy.guide.part7.reflection}</p>
                </div>
              </div>

              {/* Closing */}
              <div className="pt-4">
                <h4 className="font-serif text-lg text-[#1D2D44] mb-4">{copy.guide.closing.title}</h4>
                <div className="space-y-3 text-[#1D2D44] leading-relaxed">
                  <p>{copy.guide.closing.p1}</p>
                  <p className="font-medium text-[#1D2D44]">{copy.guide.closing.p2}</p>
                  <p>{copy.guide.closing.p3}</p>
                  <p>{copy.guide.closing.p4}</p>
                  <p>{copy.guide.closing.p5}</p>
                  <p className="font-medium text-[#1D2D44]">{copy.guide.closing.p6}</p>
                  <p className="font-serif text-[#5E7F63] italic pt-2">{copy.guide.closing.p7}</p>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

const MyReflectionsPage = () => {
  const { axiosAuth } = useAuth();
  const [reflections, setReflections] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = axiosAuth();
        const [reflectionsRes, progressRes] = await Promise.all([
          api.get('/reflections'),
          api.get('/progress')
        ]);
        setReflections(reflectionsRes.data);
        setProgress(progressRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(copy.reflections.loadError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [axiosAuth]);

  const isJourneyComplete = progress?.is_complete;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#1D2D44] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#1D2D44] font-medium">{copy.reflections.loadingReflections}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Navigation />

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        {/* Page Header */}
        <div className="text-center mb-10 animate-fade-in">
          <div className="w-16 h-16 bg-[#A3B8A6]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-[#5E7F63]" />
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1D2D44] mb-2">
            {copy.reflections.title}
          </h1>
          <p className="text-[#424E59]">
            {copy.reflections.subtitle}
          </p>
        </div>

        {reflections.length === 0 ? (
          <Card className="border-none shadow-md rounded-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-[#424E59] mb-4">
                {copy.reflections.emptyState}
              </p>
              <Link to="/dashboard">
                <Button 
                  className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full"
                  data-testid="start-journey-btn"
                >
                  {copy.reflections.startJourney}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6 stagger-children">
            {reflections.map((reflection) => (
              <Card 
                key={reflection.id}
                className="border-none shadow-md rounded-2xl overflow-hidden"
                data-testid={`reflection-card-${reflection.checkpoint_id}`}
              >
                <div className="h-1 bg-[#B89B5E]"></div>
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="mb-4">
                    <p className="text-[#5E7F63] text-sm uppercase tracking-wider">
                      {reflection.checkpoint_subtitle}
                    </p>
                    <h3 className="font-serif text-xl text-[#1D2D44]">
                      {reflection.checkpoint_name}
                    </h3>
                  </div>

                  {/* Serious Question & Answer - Read Only */}
                  <div className="mb-6">
                    <p className="text-[#3A4A5A] font-medium mb-2 text-sm">
                      {reflection.serious_question}
                    </p>
                    <div className="bg-[#F5F5F5] rounded-xl p-4">
                      <p className="text-[#1D2D44] leading-relaxed whitespace-pre-wrap">
                        {reflection.serious_answer}
                      </p>
                    </div>
                  </div>

                  {/* Lighter Question & Answer - Always shown */}
                  <div className="border-t border-[#E5E5E5] pt-4">
                    <p className="text-[#5E7F63] text-sm mb-2 italic">
                      {reflection.lighter_question}
                    </p>
                    {reflection.lighter_answer ? (
                      <p className="text-[#1D2D44] text-sm leading-relaxed">
                        {reflection.lighter_answer}
                      </p>
                    ) : (
                      <p className="text-[#424E59]/50 text-sm italic">
                        {copy.reflections.notAnswered}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Guidance Section - Only shown after completing all checkpoints */}
        {isJourneyComplete && <GuidanceSection />}

        <p className="text-center text-[#424E59] text-sm mt-10">
          {copy.reflections.privacyNote}
        </p>
      </main>
    </div>
  );
};

export default MyReflectionsPage;
